import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { App } from "./App";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { SWRProvider } from "./providers/SWRProvider";
import { ThemeProvider } from "./providers/ThemeProvider";
import "@/styles/index.css";

const APP_HEIGHT_VAR = "--app-height";
const editableSelector = "input, textarea, select, [contenteditable='true']";

const isEditableElement = (element: Element | null): boolean => {
	if (!element) return false;
	if (!(element instanceof HTMLElement)) return false;
	return element.matches(editableSelector);
};

let stableAppHeight = Math.max(1, Math.round(window.innerHeight));
let syncFrame = 0;

const resetPageScroll = () => {
	if (isEditableElement(document.activeElement)) return;
	if (window.scrollX || window.scrollY) {
		window.scrollTo({ behavior: "auto", left: 0, top: 0 });
	}
	document.documentElement.scrollTop = 0;
	document.body.scrollTop = 0;
};

const syncAppViewportHeight = () => {
	const layoutHeight = Math.max(1, Math.round(window.innerHeight));
	if (!isEditableElement(document.activeElement)) {
		stableAppHeight = layoutHeight;
	}

	document.documentElement.style.setProperty(APP_HEIGHT_VAR, `${stableAppHeight}px`);
	if (!isEditableElement(document.activeElement)) {
		resetPageScroll();
	}
};

const requestAppViewportSync = () => {
	if (syncFrame) window.cancelAnimationFrame(syncFrame);
	syncFrame = window.requestAnimationFrame(() => {
		syncFrame = 0;
		syncAppViewportHeight();
	});
};

const syncAfterKeyboardCloses = () => {
	for (const delay of [80, 240, 480]) {
		window.setTimeout(() => {
			if (!isEditableElement(document.activeElement)) {
				syncAppViewportHeight();
			}
		}, delay);
	}
};

const standaloneQuery = window.matchMedia("(display-mode: standalone)");

const syncStandaloneMode = () => {
	const navigatorWithStandalone = navigator as Navigator & { standalone?: boolean };
	const isStandalone = standaloneQuery.matches || Boolean(navigatorWithStandalone.standalone);
	document.documentElement.classList.toggle("is-standalone", isStandalone);
};

const registerServiceWorker = () => {
	if (!("serviceWorker" in navigator) || !import.meta.env.PROD) {
		return;
	}

	window.addEventListener("load", () => {
		void navigator.serviceWorker.register("/sw.js").catch(() => undefined);
	});
};

syncAppViewportHeight();
syncStandaloneMode();
registerServiceWorker();

window.addEventListener("resize", requestAppViewportSync);
window.addEventListener("scroll", resetPageScroll, { passive: true });
window.addEventListener("orientationchange", () => {
	window.setTimeout(syncAppViewportHeight, 260);
});
document.addEventListener("focusout", (event) => {
	if (!isEditableElement(event.target as Element | null)) return;
	syncAfterKeyboardCloses();
});
standaloneQuery.addEventListener("change", syncStandaloneMode);

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
	<React.StrictMode>
		<ThemeProvider>
			<SWRProvider>
				<BrowserRouter>
					<ErrorBoundary>
						<App />
					</ErrorBoundary>
				</BrowserRouter>
			</SWRProvider>
		</ThemeProvider>
	</React.StrictMode>,
);
